import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { Order } from '@/types/sorting'
import type { DeepCamelCase } from '@/types/utils'
import { getOrdering } from '@/utils/get-ordering'
import { requiredString, studentClass } from '@/utils/zod'

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    student_class: studentClass.nullable().optional(),
    excused_lessons_count: z.number(),
    unexcused_lessons_count: z.number(),
    total_absences_count: z.number(),
    lateness_lessons_count: z.number()
  })
)

type APIResponse = z.infer<typeof responseSchema>
type StudentAbsenceList = DeepCamelCase<APIResponse>
export type StudentAbsence = StudentAbsenceList[number]

const parseResponse = (
  response: z.infer<typeof responseSchema>
): StudentAbsence[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    studentClass: item.student_class
      ? {
          id: item.student_class.id,
          displayName: item.student_class.display_name
        }
      : undefined,
    excusedLessonsCount: item.excused_lessons_count,
    unexcusedLessonsCount: item.unexcused_lessons_count,
    totalAbsencesCount: item.total_absences_count,
    latenessLessonsCount: item.lateness_lessons_count
  }))

export type StudentAbsenceParams = {
  classId?: string[]
  studentId?: string[]
  semester?: string
  pageSize?: number
  page?: number
  sort?: string
  order?: Order
}

const useStudentAbsences = (params: StudentAbsenceParams = {}) =>
  useQuery({
    queryKey: ['panelAbsencesSummaryRetrieve', params],
    staleTime: 60 * 100,
    queryFn: async () => {
      const response = await httpService.panel.panelAbsencesSummaryRetrieve({
        class_id: params.classId,
        student_id: params.studentId,
        semester_id: params.semester,
        page_size: params.pageSize,
        page: params.page,
        ordering: getOrdering(params),
        fetchKeys: {
          id: true,
          name: true,
          student_class: true,
          excused_lessons_count: true,
          unexcused_lessons_count: true,
          total_absences_count: true,
          lateness_lessons_count: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return {
        list: parsed,
        count: response.count
      }
    },
    enabled: !!params.semester
  })

export default useStudentAbsences
